import { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Dialog, Typography } from '@mui/material';
import passKnowCloseOuterSrc from '../../assets/bazzar/pass-know-close-outer.png';
import passKnowCloseXSrc from '../../assets/bazzar/pass-know-close-x.png';
import { fetchAboutXpContent } from '../../services/bazaarService';

const PANEL_MAX_WIDTH = 1235;
const HEADER_HEIGHT = 64;
const INNER_GRADIENT =
  'radial-gradient(ellipse 61.75% 25.3% at 50% 50%, #3d03a1 63.46%, #2f027e 81.73%, #22005c 100%)';

const sectionHeadingSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: 20,
  lineHeight: '32px',
  letterSpacing: '0.8px',
  color: '#fff',
  m: 0,
};

const bodyTextSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: '0.5px',
  color: '#fff',
  m: 0,
};

const richContentSx = {
  maxWidth: 1159,
  color: '#fff',
  '& h1, & h2, & h3': {
    ...sectionHeadingSx,
    mt: 3,
    mb: 1.5,
    '&:first-of-type': { mt: 0 },
  },
  '& p': {
    ...bodyTextSx,
    mb: 1.5,
  },
  '& ul, & ol': {
    m: 0,
    mb: 1.5,
    pl: 3,
    color: '#fff',
    '& li': bodyTextSx,
    '& li p': { m: 0 },
  },
  '& a': {
    color: '#ff00f2',
    textDecoration: 'underline',
  },
  '& strong': {
    fontWeight: 700,
  },
};

function pickString(item, keys, fallback = '') {
  for (const key of keys) {
    if (item?.[key] != null && String(item[key]).trim()) return String(item[key]).trim();
  }
  return fallback;
}

function stringifyContent(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  return '';
}

function getListItems(value) {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => {
      if (typeof item === 'string' || typeof item === 'number') return String(item).trim();
      return pickString(item, ['title', 'text', 'label', 'description', 'content', 'body']);
    })
    .filter(Boolean);
}

function hasHtml(value) {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

function unwrapPayload(payload) {
  let current = payload;

  for (let index = 0; index < 3; index += 1) {
    if (!current || typeof current !== 'object' || Array.isArray(current)) break;
    if (!('data' in current)) break;

    current = current.data;
  }

  return current;
}

function normalizeSection(section) {
  if (typeof section === 'string') {
    return { heading: '', body: section.trim(), items: [] };
  }

  if (!section || typeof section !== 'object') return null;

  const listOptions = [
    getListItems(section.items),
    getListItems(section.list),
    getListItems(section.points),
    getListItems(section.benefits),
  ];
  const directItems = listOptions.find((items) => items.length) || [];
  const bodyValue =
    section.description ??
    section.content ??
    section.body ??
    section.text ??
    section.answer ??
    section.details;
  const body = Array.isArray(bodyValue) ? '' : stringifyContent(bodyValue);
  const bodyItems = Array.isArray(bodyValue) ? getListItems(bodyValue) : [];

  return {
    heading: pickString(section, ['heading', 'title', 'question', 'name', 'label']),
    body,
    items: directItems.length ? directItems : bodyItems,
  };
}

function normalizeAboutContent(payload, fallback) {
  const data = unwrapPayload(payload);

  if (typeof data === 'string') {
    return hasHtml(data)
      ? { ...fallback, html: data }
      : { ...fallback, sections: [{ heading: '', body: data, items: [] }] };
  }

  if (Array.isArray(data)) {
    const sections = data.map(normalizeSection).filter(Boolean);
    return sections.length ? { ...fallback, sections } : fallback;
  }

  if (!data || typeof data !== 'object') return fallback;

  const title = pickString(data, ['title', 'heading', 'name'], fallback.title);
  const html = pickString(data, ['html', 'htmlContent', 'contentHtml', 'descriptionHtml']);

  if (html) return { ...fallback, title, html };

  const possibleSections = data.items ?? data.sections ?? data.section ?? data.contentSections ?? data.about;
  const sections = Array.isArray(possibleSections)
    ? possibleSections.map(normalizeSection).filter(Boolean)
    : [];
  const passInfoSection = normalizeSection(data.passInfo);

  if (passInfoSection) {
    sections.push(passInfoSection);
  }

  if (sections.length) return { ...fallback, title, sections };

  const content = pickString(data, ['content', 'description', 'body', 'text']);
  if (content) {
    return hasHtml(content)
      ? { ...fallback, title, html: content }
      : { ...fallback, title, sections: [{ heading: '', body: content, items: [] }] };
  }

  return { ...fallback, title };
}

function CloseButton({ onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label="Close"
      sx={{
        position: 'relative',
        width: 36,
        height: 36,
        flexShrink: 0,
        border: 0,
        borderRadius: '100px',
        p: 0,
        cursor: 'pointer',
        background: 'linear-gradient(0deg, #5a04d7 0%, #ff00f2 100%)',
      }}
    >
      <Box
        component="img"
        src={passKnowCloseOuterSrc}
        alt=""
        aria-hidden="true"
        sx={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: 24,
          height: 24,
          transform: 'translate(-50%, -50%)',
          display: 'block',
          pointerEvents: 'none',
        }}
      />
      <Box
        component="img"
        src={passKnowCloseXSrc}
        alt=""
        aria-hidden="true"
        sx={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: 12,
          height: 12,
          transform: 'translate(-50%, -50%)',
          display: 'block',
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
}

function AboutSection({ section, index }) {
  const heading = section.heading ? `${index + 1}. ${section.heading}` : '';

  return (
    <Box>
      {heading ? (
        <Typography component="h3" sx={sectionHeadingSx}>
          {heading}
        </Typography>
      ) : null}
      {section.body ? <Typography sx={{ ...bodyTextSx, mt: heading ? 1.5 : 0 }}>{section.body}</Typography> : null}
      {section.items?.length ? (
        <Box
          component="ol"
          sx={{
            m: 0,
            mt: 1.5,
            pl: '21px',
            color: '#fff',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            fontSize: 14,
            lineHeight: '28px',
            letterSpacing: '0.5px',
          }}
        >
          {section.items.map((item) => (
            <Box component="li" key={item} sx={{ mb: 0 }}>
              {item}
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

export default function AboutXpKnowMoreDialog({ open, onClose, slug, fallbackContent }) {
  const [remoteContent, setRemoteContent] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !slug) return undefined;

    const controller = new AbortController();
    setLoading(true);

    fetchAboutXpContent(slug, { signal: controller.signal })
      .then((content) => setRemoteContent(content))
      .catch((error) => {
        if (error?.name !== 'CanceledError' && error?.code !== 'ERR_CANCELED') {
          setRemoteContent(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [open, slug]);

  const content = useMemo(
    () => normalizeAboutContent(remoteContent, fallbackContent),
    [fallbackContent, remoteContent]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'center',
          justifyContent: 'center',
        },
        '& .MuiBackdrop-root': {
          backdropFilter: 'blur(20px)',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
      }}
      PaperProps={{
        sx: {
          m: 2,
          width: '100%',
          maxWidth: PANEL_MAX_WIDTH,
          bgcolor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          borderRadius: '36px',
          border: '1px solid #d6a7ff',
          background: 'linear-gradient(180deg, #8f51fb 0%, #5c1ec8 34.483%)',
          boxShadow: '0 0 16px 6px rgba(183, 89, 255, 0.35)',
          overflow: 'hidden',
          maxHeight: 'calc(100vh - 32px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            minHeight: HEADER_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: { xs: 2.5, sm: '38px' },
            py: 2,
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: { xs: 20, sm: 24 },
              lineHeight: '32px',
              letterSpacing: '0.8px',
              color: '#fff',
              textShadow: '0 2px 0 rgba(0, 0, 0, 0.8)',
            }}
          >
            {content.title}
          </Typography>

          <CloseButton onClick={onClose} />
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            background: INNER_GRADIENT,
            boxShadow: 'inset 0 12px 10px rgba(43, 0, 118, 0.39)',
            px: { xs: 2.5, sm: '38px' },
            pt: 2.5,
            pb: { xs: 3, sm: 4 },
          }}
        >
          {loading && !remoteContent ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: '#ff00f2' }} />
            </Box>
          ) : content.html ? (
            <Box sx={richContentSx} dangerouslySetInnerHTML={{ __html: content.html }} />
          ) : (
            <Box sx={{ maxWidth: 1159, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {(content.sections || []).map((section, index) => (
                <AboutSection key={`${section.heading}-${index}`} section={section} index={index} />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
